import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Page } from '@sisoma/api';
import { firstValueFrom } from 'rxjs';

import { posterUrl, type MediaKind } from './cloudinary-upload.service';
import { type ContentMedia } from './content-media';
import { MEDIA_CONFIG } from './media.config';

/**
 * The stock providers the API proxies —
 * `MEDIA_PROVIDER_IDS` (ts-api `_services/external-media/providers/media-provider.interface.ts`).
 *
 * Note **`unsplash`**, not legacy's `unSplash`: the camel-cased name is the
 * legacy *service* class and its `selectedLibrary` key, but the route segment
 * has always been lower case (`un-splash.service.ts:24`).
 */
export type StockProvider = 'pexels' | 'unsplash' | 'pixabay' | 'giphy';

/**
 * The three photo libraries. Split out from {@link StockProvider} because they
 * are the only sources whose picks are **imported into Cloudinary** rather than
 * attached as they stand — see {@link MediaPick}.
 */
export type StockPhotoProvider = Exclude<StockProvider, 'giphy'>;

/** Every non-device source the picker offers. */
export type LibrarySource = 'usersMedia' | StockProvider;

/** Display names — legacy's tab labels (media-modal.component.html:38-153). */
export const SOURCE_LABELS: Readonly<Record<LibrarySource, string>> = {
  usersMedia: 'My Media',
  pexels: 'Pexels',
  unsplash: 'UnSplash',
  pixabay: 'Pixabay',
  giphy: 'Gifs by Giphy',
};

/**
 * What the picker hands back for one chosen thing, tagged by **how it
 * attaches** — the three mechanisms legacy's `uploadProcess` switches between
 * (crud-content.component.ts:442-455):
 *
 * - `library` (My Media and Giphy) is **attached as it stands** — legacy's
 *   `isFromLibrary` short-circuit, which returns the media object untouched.
 * - `import` (the stock photo providers) goes through Cloudinary: legacy falls
 *   through to `uploadImageToCloudinary` → `uploadMediaToCloudinary`, whose
 *   `mediaFile instanceof Blob ? mediaFile : mediaFile?.url`
 *   (cloudinary.service.ts:176) posts the provider's URL as the `file` field
 *   and lets Cloudinary fetch it server-side.
 * - `device` is the Upload tab, uploading the local file.
 *
 * `source` names where a pick came from, for the failure message.
 */
export type MediaPick =
  | { readonly from: 'device'; readonly file: File }
  | {
      readonly from: 'library';
      readonly source: 'usersMedia' | 'giphy';
      readonly media: ContentMedia;
    }
  | { readonly from: 'import'; readonly source: StockPhotoProvider; readonly url: string };

/** One tile in a library grid. */
export interface LibraryItem {
  /** Grid key and selection identity — unique within a source. */
  readonly key: string;
  /** What the tile draws. */
  readonly thumbUrl: string;
  /** What the Selected strip calls it. */
  readonly name: string;
  /**
   * Set only for assets that already have a Cloudinary public id, which is what
   * the "already attached" check matches on. Stock photos have none until they
   * are imported.
   */
  readonly mediaPublicId?: string;
  /** What {@link MediaPick} this tile produces when chosen. */
  readonly pick: MediaPick;
}

/**
 * One page of a library.
 *
 * `next` is opaque: the API's own `nextCursor` for the stock providers, and the
 * next **page index** for `user-media-library`, which is page-numbered. `null`
 * means there is no more.
 */
export interface LibraryPage {
  readonly items: readonly LibraryItem[];
  readonly next: string | null;
}

/**
 * Legacy's page size for every library — `count = 16` on each of the five
 * services (users-media.service.ts:46, pexels.service.ts:60, …). Well inside
 * `PageOptionsDto`'s `@Max(50)` and `FeaturedMediaDto`'s `@Max(80)`.
 */
export const LIBRARY_PAGE_SIZE = 16;

/** One row of `GET /user-media-library`. Every field is optional — see the class doc. */
interface MediaLibraryRow {
  url?: string;
  mediaPublicId?: string;
  type?: string;
  previewUrl?: string;
  pages?: number;
  documentType?: string;
  width?: number;
  height?: number;
  createdOn?: string | null;
}

/** One item of `GET /external-media/:provider/{search,featured}` — `ExternalMediaItem`. */
interface ExternalMediaItem {
  id: string;
  provider?: StockProvider;
  type?: 'image' | 'video';
  url?: string;
  previewUrl?: string;
  width?: number;
  height?: number;
}

interface ExternalMediaPage {
  items?: ExternalMediaItem[];
  nextCursor?: string | null;
  total?: number;
}

/**
 * The picker's two read-only libraries: the acting user's own media, and the
 * stock providers the API proxies.
 *
 * ## Both endpoints are ungated
 *
 * `MediaLibraryController` (`users/my-content/media-library/media-library.controller.ts`)
 * and `ExternalMediaController` (`_services/external-media/external-media.controller.ts`)
 * declare no `@Roles`, no `@CreatorRoles` and no capability, so the global
 * `AuthenticationGuard`'s Bearer default is the only gate. A partner member
 * reaches both today.
 *
 * ## What "My Media" is scoped to
 *
 * `MATCH (u:Users {id: $sub})<-[:CREATED_BY]-(doc:Images|Media)` — the **acting
 * user's** own uploads, keyed on the token's `sub`. There is no partner-scoped
 * media library, and this is deliberately not pretending there is one: a partner
 * member sees exactly what they would see in the users app.
 *
 * ## Row shapes are pinned to the producer, and the producer stores very little
 *
 * `MediaLibraryService.create` persists **four** properties —
 * `{ url, mediaPublicId, type, createdOn }` — and `findAll` returns the node's
 * raw properties. So `previewUrl`, `pages`, `documentType`, `width` and `height`
 * are present only on older `:Images` nodes; everything here derives what it
 * needs from the url when they are missing, which is what legacy's own two
 * fallbacks do (users-media.service.ts:132-134 and
 * media-library.component.ts:84-86).
 *
 * ## No provider keys reach the browser
 *
 * Every stock call goes through `{apiUrl}/external-media/:provider/…`. None of
 * the legacy services hold a key either — they were moved behind the proxy
 * already (see the note at the top of `external-media.types.ts`).
 */
@Injectable()
export class MediaLibraryService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(MEDIA_CONFIG).apiUrl;

  /**
   * One page of the acting user's media library, filtered to `kind`.
   *
   * `category` is the API's own filter (`toLower(doc.type) = 'image' | 'video' |
   * 'document'`), which is why this sends the drawer's `MediaKind` rather than
   * legacy's `designType`: legacy passes the raw post type, so a caption post
   * asks for `category=caption`, matches nothing, and then stamps
   * `type: 'caption'` onto every row it did get back (users-media.service.ts:71,
   * :123).
   */
  async myMedia(kind: MediaKind, cursor: string | null): Promise<LibraryPage> {
    const page = cursor ? Number(cursor) : 0;

    const response = await firstValueFrom(
      this.http.get<Page<MediaLibraryRow>>(`${this.api}/user-media-library`, {
        params: new HttpParams()
          .set('page', page)
          .set('limit', LIBRARY_PAGE_SIZE)
          .set('category', kind),
      }),
    );

    const items = (response?.data ?? [])
      .map((row) => toLibraryItem(row, kind))
      .filter((item): item is LibraryItem => item !== null);

    return { items, next: response?.meta?.hasNextPage ? String(page + 1) : null };
  }

  /**
   * One page of a stock provider — `featured` while the search box is empty,
   * `search` once it is not.
   *
   * Legacy makes the same split for the three photo libraries
   * (pexels.service.ts:154, and `/search` without a usable query is a 400). Its
   * Giphy service is the exception: it always calls `/search` with the constant
   * `q = 'professional'` (giphy.service.ts:64), which predates the proxy growing
   * a `featured` route. Trending is what "featured" means for Giphy, so this
   * uses the route rather than the placeholder term.
   */
  async stock(
    provider: StockProvider,
    request: { query?: string | null; cursor?: string | null },
  ): Promise<LibraryPage> {
    const query = request.query?.trim() || null;
    const kind = provider === 'giphy' ? 'video' : 'image';

    let params = new HttpParams().set('limit', LIBRARY_PAGE_SIZE).set('kind', kind);
    if (query) params = params.set('query', query);
    // `FeaturedMediaDto.cursor` is `@Matches(/^\d{1,9}$/)`; replay it verbatim
    // and never invent one.
    if (request.cursor) params = params.set('cursor', request.cursor);

    const response = await firstValueFrom(
      this.http.get<ExternalMediaPage>(
        `${this.api}/external-media/${provider}/${query ? 'search' : 'featured'}`,
        { params },
      ),
    );

    const items = (response?.items ?? [])
      .map((item) => toStockItem(item, provider))
      .filter((item): item is LibraryItem => item !== null);

    return { items, next: response?.nextCursor ?? null };
  }
}

/**
 * One `user-media-library` row → a tile.
 *
 * The media element is the row's own stored asset, unchanged — legacy's
 * `uploadProcess` short-circuits on `source === 'usersMedia'` and re-attaches
 * the object as it is (crud-content.component.ts:443-455), so a re-pick costs
 * no upload and keeps the public id the library knows it by.
 *
 * `id` is omitted for the reason `toContentMedia` omits it: the content API
 * MERGEs the node on `coalesce(mediaItem.mediaPublicId, randomUUID())` and then
 * `SET m += mediaItem`, so any `id` in the payload overwrites the merge key.
 * Legacy sets `mediaLibrary-${mediaPublicId}` here (users-media.service.ts:118),
 * which is exactly that collision in slow motion.
 */
function toLibraryItem(row: MediaLibraryRow, kind: MediaKind): LibraryItem | null {
  const { url, mediaPublicId } = row;
  if (!url || !mediaPublicId) return null;

  // The API filters on `doc.type`, which older nodes may not carry; legacy adds
  // this second, url-based pass for the same reason (media-library.component.ts:81).
  const isPdf = url.toLowerCase().endsWith('.pdf');
  if (kind === 'document' ? !isPdf : isPdf) return null;

  const base = { url, mediaPublicId };
  const size = row.width && row.height ? { width: row.width, height: row.height } : {};
  const name = fileNameOf(url);

  if (kind === 'video') {
    // users-media.service.ts:132-134.
    const previewUrl = row.previewUrl || url.replace('.mp4', '.jpg');
    return tile(name, previewUrl, mediaPublicId, { ...base, ...size, type: 'video', previewUrl });
  }

  if (kind === 'document') {
    // The row's own raster if it has one, Cloudinary's first page otherwise —
    // the same derivation the drawer's own document upload stores.
    const previewUrl = row.previewUrl || posterUrl(url);
    return tile(name, previewUrl, mediaPublicId, {
      ...base,
      type: 'document',
      previewUrl,
      ...(row.documentType ? { documentType: row.documentType } : {}),
      ...(row.pages ? { pages: row.pages } : {}),
    });
  }

  return tile(name, url, mediaPublicId, { ...base, ...size, type: 'image' });
}

function tile(
  name: string,
  thumbUrl: string,
  mediaPublicId: string,
  media: ContentMedia,
): LibraryItem {
  return {
    key: mediaPublicId,
    thumbUrl,
    name,
    mediaPublicId,
    pick: { from: 'library', source: 'usersMedia', media },
  };
}

/**
 * One `external-media` item → a tile.
 *
 * **Giphy** is attached raw, as legacy does: its pick carries the provider's own
 * mp4 as `url` and the animated GIF as `previewUrl`, with `type: 'video'`
 * (`kind` defaults to video on that provider, giphy.provider.ts:19). Nothing is
 * uploaded — `uploadProcess`'s `isFromLibrary` names `'giphy'` explicitly. That
 * is also the only route a GIF has into a post: the Upload tab's `accept` list
 * admits JPEG and PNG only.
 *
 * A **stock photo** carries only its url, because nothing else about it
 * survives: the element that reaches storage is Cloudinary's response to the
 * import, so the provider's id, dimensions and — note — its `credits` are all
 * dropped. Legacy drops them too (pexels.service.ts:199-205 keeps four keys and
 * no attribution), which is why the grid credits the provider on screen instead.
 */
function toStockItem(item: ExternalMediaItem, provider: StockProvider): LibraryItem | null {
  const url = item?.url;
  if (!item?.id || !url) return null;

  const previewUrl = item.previewUrl || url;

  if (provider === 'giphy') {
    return {
      key: item.id,
      thumbUrl: previewUrl,
      name: 'Giphy GIF',
      mediaPublicId: item.id,
      pick: {
        from: 'library',
        source: 'giphy',
        media: { url, previewUrl, type: item.type ?? 'video', mediaPublicId: item.id },
      },
    };
  }

  return {
    key: item.id,
    thumbUrl: previewUrl,
    name: `${SOURCE_LABELS[provider]} image`,
    pick: { from: 'import', source: provider, url },
  };
}

/** The asset's own file name, for the Selected strip. */
function fileNameOf(url: string): string {
  const last = url.split('?')[0].split('/').pop() ?? '';
  try {
    return decodeURIComponent(last) || 'Media';
  } catch {
    return last || 'Media';
  }
}
